import { IconButtonProps } from '@chakra-ui/button';
import { ModalProps } from '@chakra-ui/modal';
import { darken } from '@chakra-ui/theme-tools';
import {
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useBreakpointValue,
  useTheme,
} from '@telescope/cassini-ui';
import { useScroll } from 'hooks';
import { FC, useEffect } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { VoteOption } from 'types';
import { isIframed } from 'utils';
import { VoteOptionDetail } from './VoteOptionDetail';

interface VoteModalProps extends Omit<ModalProps, 'children'> {
  /**
   * Callback fired when previous arrow is clicked
   */
  onPrevious(): void;
  /**
   * Callback fired when next arrow is clicked
   */
  onNext(): void;
  /**
   * Current vote option to display
   */
  voteOption: VoteOption | null;
  voteButton?: React.ReactElement;
}

export const VoteModal: FC<VoteModalProps> = (props) => {
  const { onPrevious, onNext, voteOption, voteButton, ...modalProps } = props;
  const { scrollToTop } = useScroll();

  const theme = useTheme();
  const navArrorColor = useBreakpointValue({
    base: theme.semanticTokens.colors['Modal-nav-arrows-base'],
    sm: theme.semanticTokens.colors['Modal-nav-arrows-sm'],
  });
  const navArrowProps: Partial<IconButtonProps> = {
    color: navArrorColor,
    _hover: { color: darken(navArrorColor, 20) },
    _active: { color: darken(navArrorColor, 30) },
    variant: 'link',
    top: '50%',
    position: 'absolute',
    fontSize: '3xl',
  };

  useEffect(() => {
    if (isIframed() && modalProps.isOpen) {
      scrollToTop();
    }
  }, [scrollToTop, modalProps.isOpen]);

  if (!voteOption) return null;

  return (
    <Modal {...modalProps} blockScrollOnMount={!isIframed()}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton size="lg" />
        <IconButton
          aria-label="Previous"
          icon={<FaChevronLeft />}
          left={[0, '-50px']}
          onClick={onPrevious}
          {...navArrowProps}
        />
        <IconButton
          aria-label="Next"
          icon={<FaChevronRight />}
          position="absolute"
          right={[0, '-50px']}
          onClick={onNext}
          {...navArrowProps}
        />
        <ModalBody bg="surface" color="on-surface">
          <VoteOptionDetail voteOption={voteOption} voteButton={voteButton} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
